import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities } from '../store/slices/entitySlice';
import { chatApi } from '../services/chatApi';
import { entityApi } from '../services/entityApi';
import { Entity } from '../types';
import { config } from '../config';
import { useLocation } from 'react-router-dom';

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  confidence?: number;
  entityName?: string;
  isStreaming?: boolean;
  isComplete?: boolean;
  isProcessing?: boolean;
}

const Chat: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { entities, isLoading: entitiesLoading } = useAppSelector((state) => state.entities);

  // Get passed data from navigation state
  const passedData = location.state as { selectedEntityId: string; entityName: string } | null;

  const [selectedEntityId, setSelectedEntityId] = useState<string>(passedData?.selectedEntityId || '');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useStreaming, setUseStreaming] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchEntities());
  }, [dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateMessage = (msg: string): string | null => {
    if (!msg.trim()) {
      return 'Message cannot be empty';
    }
    if (msg.length < config.validation.chatMessage.minLength) {
      return `Message must be at least ${config.validation.chatMessage.minLength} character`;
    }
    if (msg.length > config.validation.chatMessage.maxLength) {
      return `Message must be less than ${config.validation.chatMessage.maxLength} characters`;
    }
    return null;
  };

  const handleSendMessage = async () => {
    if (!selectedEntityId) {
      setError('Please select an entity to chat with');
      return;
    }

    const validationError = validateMessage(message);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsLoading(true);

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Add initial bot message for streaming
    const botMessageId = (Date.now() + 1).toString();
    if (useStreaming) {
      const initialBotMessage: ChatMessage = {
        id: botMessageId,
        type: 'bot',
        content: '...',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages(prev => [...prev, initialBotMessage]);
    }

    const currentMessage = message;
    setMessage('');

    try {
      const chatRequest: any = { // Assuming ChatRequest and ChatResponse are removed, using 'any' for now
        message: currentMessage,
        entity_id: selectedEntityId,
      };

      if (useStreaming) {
        // Use streaming chat
        setIsStreaming(true);
        try {
          await chatApi.streamChat(
            chatRequest,
            (streamData) => {
              // Handle streaming data - accumulate text as it streams in
              setMessages(prev => prev.map(msg =>
                msg.id === botMessageId
                  ? {
                    ...msg,
                    content: streamData.response || streamData.message || msg.content,
                    confidence: streamData.confidence_score,
                    entityName: streamData.entity_name,
                    isStreaming: streamData.isStreaming !== false,
                    isProcessing: streamData.isProcessing || false,
                    isComplete: streamData.isComplete || false
                  }
                  : msg
              ));
            },
            (error) => {
              setError(error);
              // Update the bot message with error
              setMessages(prev => prev.map(msg =>
                msg.id === botMessageId
                  ? {
                    ...msg,
                    content: `❌ ${error}`,
                    isStreaming: false,
                    isComplete: true
                  }
                  : msg
              ));
            },
            () => {
              // Stream complete
              setIsStreaming(false);
              setMessages(prev => prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, isStreaming: false, isComplete: true }
                  : msg
              ));
            }
          );
        } catch (error: any) {
          setError(error.message || 'Failed to start streaming chat');
          setIsStreaming(false);
          // Update the bot message with error
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId
              ? {
                ...msg,
                content: `❌ ${error.message || 'Failed to start streaming chat'}`,
                isStreaming: false,
                isComplete: true
              }
              : msg
          ));
        }
      } else {
        // Use regular chat
        const response: any = await chatApi.sendMessage(chatRequest); // Assuming ChatResponse is removed, using 'any' for now

        // Add bot response to chat
        const botMessage: ChatMessage = {
          id: botMessageId,
          type: 'bot',
          content: response.response,
          timestamp: new Date(),
          confidence: response.confidence_score,
          entityName: response.entity_name,
        };
        setMessages(prev => [...prev, botMessage]);
      }

      // Removed dispatch(showSuccess({...})) as per new_code
    } catch (error: any) {
      setError(error.message);
      // Remove the streaming message on error
      if (useStreaming) {
        setMessages(prev => prev.filter(msg => msg.id !== botMessageId));
      }
      // Removed dispatch(showError({...})) as per new_code
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  };

  const cancelStreaming = () => {
    setIsStreaming(false);
    setError('Request cancelled by user');
    // Update the bot message to show cancellation
    setMessages(prev => prev.map(msg =>
      msg.isStreaming
        ? { ...msg, content: '❌ Request cancelled', isStreaming: false, isComplete: true }
        : msg
    ));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const newEntity = await entityApi.uploadEntity(file, `Uploaded via Chat: ${file.name}`);
      dispatch(fetchEntities()); // Refresh list
      setSelectedEntityId(newEntity.entity_id);
      setIsUploading(false);
      // Optional: Add a system message
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        content: `I've processed "${file.name}". You can now ask questions about it!`,
        timestamp: new Date(),
        entityName: newEntity.name
      }]);
    } catch (err: any) {
      setError(err.message || 'File upload failed');
      setIsUploading(false);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const selectedEntity = entities.find(e => e.entity_id === selectedEntityId);

  return (
    <Box sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Chat Interface
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Interact with your AI-powered knowledge bases through natural language
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Entity Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Entity
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Choose an entity</InputLabel>
                <Select
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                  label="Choose an entity"
                  disabled={entitiesLoading}
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.entity_id} value={entity.entity_id}>
                      {entity.name || 'Unnamed Entity'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                accept=".pdf,.csv,.xlsx,.xls,.txt"
              />
              <Button
                variant="outlined"
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                onClick={triggerFileUpload}
                disabled={isUploading || entitiesLoading}
                fullWidth
                sx={{ mb: 2 }}
              >
                {isUploading ? 'Uploading & Processing...' : 'Upload Document (PDF/CSV)'}
              </Button>

              {/* Chat Mode Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={useStreaming}
                    onChange={(e) => setUseStreaming(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {useStreaming ? <SettingsIcon color="primary" /> : <ChatIcon />}
                    {useStreaming ? 'Streaming Chat' : 'Regular Chat'}
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {selectedEntity && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid rgba(28, 233, 224, 0.25)', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {selectedEntity.name || 'Unnamed'}
                  </Typography>
                  {selectedEntity.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Description:</strong> {selectedEntity.description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Created:</strong> {new Date(selectedEntity.created_at).toLocaleDateString()}
                  </Typography>

                  {/* Processing Status */}
                  {selectedEntity.processing_status && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Processing Status:</strong>
                      </Typography>
                      <Chip
                        label={selectedEntity.processing_status === 'completed' ? 'AI Ready' :
                          selectedEntity.processing_status === 'processing' ? 'Processing' :
                            selectedEntity.processing_status === 'failed' ? 'Failed' :
                              selectedEntity.processing_status === 'pending' ? 'Pending' : 'Unknown'}
                        color={selectedEntity.processing_status === 'completed' ? 'success' :
                          selectedEntity.processing_status === 'processing' ? 'warning' :
                            selectedEntity.processing_status === 'failed' ? 'error' : 'default'}
                        size="small"
                        icon={<PsychologyIcon />}
                      />
                    </Box>
                  )}

                  {/* Last Processed */}
                  {selectedEntity.last_processed && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Last Processed:</strong> {new Date(selectedEntity.last_processed).toLocaleDateString()}
                    </Typography>
                  )}

                  {/* Data Length */}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Content Length:</strong> {selectedEntity.data?.length || 0} characters
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                onClick={clearChat}
                disabled={messages.length === 0}
                sx={{ mt: 2 }}
                fullWidth
              >
                Clear Chat
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Chat Interface */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: { xs: '70vh', md: 600 }, display: 'flex', flexDirection: 'column' }}>
            {/* Chat Messages */}
            <Box
              sx={{
                flex: 1,
                p: 2,
                overflowY: 'auto',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {messages.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* Removed BotIcon import, using SettingsIcon as a placeholder */}
                  <SettingsIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">
                    {selectedEntityId ? 'Start a conversation!' : 'Select an entity to begin chatting'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {useStreaming ? 'Streaming chat provides real-time responses' : 'Regular chat for standard responses'}
                  </Typography>
                </Box>
              ) : (
                messages.map((msg, index) => (
                  // Removed Slide import, using Fade and Slide as a placeholder
                  <Fade
                    key={msg.id}
                    in={true}
                    timeout={300 + index * 100}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          maxWidth: '70%',
                          flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                        }}
                      >
                        {/* Removed PersonIcon import, using SettingsIcon as a placeholder */}
                        <SettingsIcon
                          sx={{
                            bgcolor: msg.type === 'user' ? 'primary.main' : 'secondary.main',
                            width: 32,
                            height: 32,
                          }}
                        />
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            bgcolor: msg.type === 'user' ? 'primary.main' : 'background.paper',
                            color: msg.type === 'user' ? 'primary.contrastText' : 'text.primary',
                            borderRadius: 2,
                            border: msg.isStreaming ? '2px dashed' : '1px solid',
                            borderColor: msg.isStreaming ? 'primary.main' : 'rgba(28, 233, 224, 0.25)',
                          }}
                        >
                          <Box sx={{
                            '& p': { m: 0, mb: 1 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 'bold', color: 'inherit' },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& code': { bgcolor: 'rgba(0,0,0,0.1)', p: 0.5, borderRadius: 1, fontFamily: 'monospace' },
                            '& pre': { bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1, overflowX: 'auto' },
                            '& blockquote': { borderLeft: '3px solid #ccc', pl: 2, ml: 0, fontStyle: 'italic' },
                            '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                            '& th, & td': { border: '1px solid #ddd', p: 1 },
                            '& a': { color: 'inherit', textDecoration: 'underline' }
                          }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {msg.content}
                            </ReactMarkdown>
                          </Box>
                          <Typography component="div" variant="body2" sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            {msg.isStreaming && !msg.isComplete && (
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                <span style={{ opacity: 0.7 }}>typing...</span>
                              </Box>
                            )}
                            {msg.isProcessing && (
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                <span style={{ opacity: 0.7 }}>processing...</span>
                              </Box>
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                opacity: 0.7,
                                fontSize: '0.7rem',
                              }}
                            >
                              {msg.timestamp.toLocaleTimeString()}
                            </Typography>
                            {/* Confidence chip removed */}
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  </Fade>
                ))
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Cancel Streaming Button */}
              {isStreaming && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={cancelStreaming}
                    startIcon={<CancelIcon />}
                    size="small"
                  >
                    Cancel Request
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder={selectedEntityId ? "Type your message..." : "Select an entity first"}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={!selectedEntityId || isLoading || isStreaming}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 44,
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!selectedEntityId || !message.trim() || isLoading || isStreaming}
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    p: 0,
                    alignSelf: 'center'
                  }}
                >
                  {isLoading || isStreaming ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Press Enter to send, Shift+Enter for new line
                {useStreaming && ' • Streaming mode enabled'}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Chat;
